import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaSearch, FaMapMarkerAlt, FaArrowDown, FaBuilding,
  FaCalendarAlt, FaWeightHanging, FaInbox,
  FaChevronLeft, FaChevronRight, FaTruck,
} from 'react-icons/fa'
import { fetchMyRequests } from '../../store/slices/transportRequestSlice'
import './TransportRequest.scss'

const fade = {
  hidden  : { opacity: 0, y: 12 },
  visible : i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const STATUS_COLORS = {
  invited            : { mod: 'gray',   label: 'Invited'    },
  viewed             : { mod: 'blue',   label: 'Viewed'     },
  proposal_submitted : { mod: 'yellow', label: 'Proposal'   },
  accepted           : { mod: 'green',  label: 'Accepted'   },
  negotiating        : { mod: 'purple', label: 'Negotiating'},
}

const TransportRequestList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { requests = [], loading, pagination } = useSelector(s => s.transportRequest)

  const [statusFilter, setStatusFilter] = useState('all')
  const [search,       setSearch]       = useState('')
  const [currentPage,  setCurrentPage]  = useState(1)

  useEffect(() => {
    const filters = { page: currentPage, limit: 12 }
    if (statusFilter !== 'all') filters.status = statusFilter
    dispatch(fetchMyRequests(filters))
  }, [dispatch, statusFilter, currentPage])

  const filtered = requests.filter(r =>
    r.request_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.origin?.toLowerCase().includes(search.toLowerCase()) ||
    r.destination?.toLowerCase().includes(search.toLowerCase()) ||
    r.contractor_company?.toLowerCase().includes(search.toLowerCase())
  )

  const totalReqs      = requests.length
  const activeReqs     = requests.filter(r => !['accepted'].includes(r.my_proposal_status)).length
  const acceptedReqs   = requests.filter(r => r.my_proposal_status === 'accepted').length

  const tabs = [
    { key: 'all',                label: 'All'         },
    { key: 'invited',            label: 'Invited'     },
    { key: 'viewed',             label: 'Viewed'      },
    { key: 'proposal_submitted', label: 'Proposals'   },
    { key: 'negotiating',        label: 'Negotiating' },
    { key: 'accepted',           label: 'Accepted'    },
  ]

  return (
    <div className="trl-page">

      {/* Header */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={0} className="trl-header">
        <div className="trl-header-left">
          <div className="trl-header-icon"><FaTruck /></div>
          <div>
            <h4 className="trl-title">Transport Requests</h4>
            <p className="trl-sub">Track and manage your active transport requests</p>
          </div>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Requests', val: totalReqs,    mod: 'blue',  icon: <FaTruck />,       note: 'All assigned requests'  },
          { label: 'In Progress',    val: activeReqs,   mod: 'yellow', icon: <FaWeightHanging />, note: 'Awaiting action'     },
          { label: 'Accepted',       val: acceptedReqs, mod: 'green',  icon: <FaBuilding />,    note: 'Deals confirmed'       },
        ].map((k, i) => (
          <Col xs={12} md={4} key={k.label}>
            <motion.div initial="hidden" animate="visible" variants={fade} custom={i + 1} className={`trl-kpi trl-kpi--${k.mod}`}>
              <div className="trl-kpi-left">
                <span className="trl-kpi-val">{k.val}</span>
                <span className="trl-kpi-label">{k.label}</span>
                <span className="trl-kpi-note">{k.note}</span>
              </div>
              <span className="trl-kpi-icon">{k.icon}</span>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* Filter Bar */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={4} className="trl-filter-bar">
        <div className="trl-search-wrap">
          <FaSearch className="trl-search-icon" />
          <input className="trl-search" placeholder="Search by ID, origin, destination…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="trl-filter-tabs">
          {tabs.map(t => (
            <button key={t.key} className={`trl-tab${statusFilter === t.key ? ' trl-tab--active' : ''}`} onClick={() => { setStatusFilter(t.key); setCurrentPage(1) }}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="trl-count">{filtered.length} request{filtered.length !== 1 ? 's' : ''}</span>
      </motion.div>

      {/* List */}
      {loading ? (
        <div className="trl-loading">
          <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading requests…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="trl-empty">
          <div className="trl-empty-icon"><FaInbox /></div>
          <p className="trl-empty-title">{search || statusFilter !== 'all' ? 'No matching requests' : 'No requests yet'}</p>
          <p className="trl-empty-sub">{search || statusFilter !== 'all' ? 'Try adjusting your search or filters.' : 'Transport requests assigned to you will appear here.'}</p>
        </div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filtered.map((req, i) => {
              const st      = STATUS_COLORS[req.my_proposal_status] || { mod: 'gray', label: req.my_proposal_status || 'New' }
              const loadDate = req.loading_date
                ? new Date(req.loading_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—'

              return (
                <Col xs={12} sm={6} md={4} xl={3} key={req.request_id}>
                  <motion.div
                    className="trl-card"
                    initial="hidden" animate="visible" exit={{ opacity: 0, y: -8 }}
                    variants={fade} custom={i}
                    layout
                    onClick={() => navigate(`/transport-request/${req.request_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="trl-card-accent" />
                    <div className="trl-card-head">
                      <div className="trl-card-meta">
                        <span className="trl-card-number">{req.request_number}</span>
                        <span className={`trl-badge trl-badge--${st.mod}`}>{st.label}</span>
                      </div>
                    </div>

                    <div className="trl-route">
                      <div className="trl-route-row">
                        <FaMapMarkerAlt className="trl-route-dot trl-route-dot--from" />
                        <span className="trl-route-place">{req.origin}</span>
                      </div>
                      <div className="trl-route-arrow"><FaArrowDown /></div>
                      <div className="trl-route-row">
                        <FaMapMarkerAlt className="trl-route-dot trl-route-dot--to" />
                        <span className="trl-route-place">{req.destination}</span>
                      </div>
                    </div>

                    <div className="trl-card-details">
                      <div className="trl-card-row">
                        <FaBuilding className="trl-card-icon" />
                        <span className="trl-card-val" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.creator_entity_name}</span>
                      </div>
                      <div className="trl-card-row">
                        <FaCalendarAlt className="trl-card-icon" />
                        <span className="trl-card-val">{loadDate}</span>
                      </div>
                      <div className="trl-card-row">
                        <FaWeightHanging className="trl-card-icon" />
                        <span className="trl-card-val">{req.total_quantity} {req.quantity_unit}</span>
                      </div>
                    </div>

                    <div className="trl-card-footer">
                      <button className="trl-card-btn trl-card-btn--view" onClick={e => { e.stopPropagation(); navigate(`/transport-request/${req.request_id}`) }}>
                        View Details
                      </button>
                    </div>
                  </motion.div>
                </Col>
              )
            })}
          </AnimatePresence>
        </Row>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <motion.div initial="hidden" animate="visible" variants={fade} custom={10} className="trl-pagination">
          <button className="trl-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
            <FaChevronLeft />
          </button>
          <span className="trl-page-info">Page {currentPage} of {pagination.totalPages}</span>
          <button className="trl-page-btn" disabled={currentPage === pagination.totalPages} onClick={() => setCurrentPage(p => p + 1)}>
            <FaChevronRight />
          </button>
        </motion.div>
      )}

    </div>
  )
}

export default TransportRequestList
